<template>
  <div class="wrapper wrapper-content animated fadeInUp">
    <div class="row">
      <div class="col-lg-12">
        <div class="ibox">
          <div class="ibox-title">
            <h2>{{ accountTitle }}</h2>
            <ol class="breadcrumb">
              <li>
                <router-link to="/">Home</router-link>
              </li>
              <li>
                <router-link to="/allegro/accounts/list"
                  >Allegro - Moje konta</router-link
                >
              </li>
              <li class="active">
                <strong>{{ accountTitle }}</strong>
              </li>
            </ol>
            <div class="ibox-tools"></div>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-lg-12">
        <div class="tabs-container allegro-account-tabs-container">
          <ul class="nav nav-tabs allegro-account-tabs" role="tablist">
            <li
              :class="{ active: activeTab === 'overview' }"
              role="presentation"
            >
              <router-link
                :to="`/allegro/accounts/${accountPk}/overview`"
                role="tab"
              >
                <i class="fa fa-eye"></i>
                <span class="tab-label">Podgląd</span>
              </router-link>
            </li>
            <li
              v-if="canViewAnySettings"
              :class="{ active: activeTab === 'settings' }"
              role="presentation"
            >
              <router-link
                :to="`/allegro/accounts/${accountPk}/settings`"
                role="tab"
              >
                <i class="fa fa-cog"></i>
                <span class="tab-label">Ustawienia</span>
                <span
                  v-if="hasMissingSettings"
                  class="label label-warning tab-badge"
                >
                  <span class="fa fa-exclamation"></span>
                </span>
              </router-link>
            </li>
            <li
              v-if="canViewOffers"
              :class="{ active: activeTab === 'offers' }"
              role="presentation"
            >
              <router-link
                :to="`/allegro/accounts/${accountPk}/offers`"
                role="tab"
              >
                <i class="fa fa-tag"></i>
                <span class="tab-label">Oferty</span>
                <span class="label label-default tab-badge">
                  {{ accountData?.stats?.active_offers || 0 }}
                </span>
              </router-link>
            </li>
            <li
              v-if="canViewThreads"
              :class="{ active: activeTab === 'threads' }"
              role="presentation"
            >
              <router-link
                :to="`/allegro/accounts/${accountPk}/threads`"
                role="tab"
              >
                <i class="fa fa-envelope"></i>
                <span class="tab-label">Wiadomości</span>
                <span
                  class="label tab-badge"
                  :class="
                    accountData?.stats?.unread_threads > 0
                      ? 'label-warning'
                      : 'label-default'
                  "
                >
                  {{ accountData?.stats?.unread_threads || 0 }}
                </span>
              </router-link>
            </li>
          </ul>

          <div class="tab-content">
            <div class="tab-pane active">
              <div class="panel-body">
                <slot />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { hasPermission } from "../../permissions";
import {
  allegroConfigPermissionsConst,
  allegroSellerPermissionsConst,
  allegroSenderPermissionsConst,
  allegroCodReceiverPermissionsConst,
  allegroResponsibleProducerPermissionsConst,
  allegroSafetyInformationPermissionsConst,
  allegroOfferPermissionsConst,
  allegroThreadPermissionsConst,
} from "../../constants/allegroConstants";

export default {
  name: "AllegroAccountHeader",
  props: {
    accountPk: { type: [String, Number], required: true },
    accountData: {
      type: Object,
      default: () => ({}),
    },
    activeTab: {
      type: String,
      default: "overview",
    },
  },
  computed: {
    accountTitle() {
      return this.accountData?.name || `Konto #${this.accountPk}`;
    },
    // Tabs are hidden without the matching permission, as in the old admin
    // panel. The settings tab needs any of the settings sections.
    canViewAnySettings() {
      return (
        hasPermission(allegroConfigPermissionsConst.view) ||
        hasPermission(allegroSellerPermissionsConst.view) ||
        hasPermission(allegroSenderPermissionsConst.view) ||
        hasPermission(allegroCodReceiverPermissionsConst.view) ||
        hasPermission(allegroResponsibleProducerPermissionsConst.view) ||
        hasPermission(allegroSafetyInformationPermissionsConst.view)
      );
    },
    canViewOffers() {
      return hasPermission(allegroOfferPermissionsConst.view);
    },
    canViewThreads() {
      return hasPermission(allegroThreadPermissionsConst.view);
    },
    hasMissingSettings() {
      const config = this.accountData?.configuration;
      const statuses = this.accountData?.settings_fill_statuses;
      if (statuses) {
        return Object.values(statuses).some((status) => status === false);
      }
      return (
        !config ||
        !config.pk ||
        !this.accountData?.seller ||
        !this.accountData?.sender ||
        !this.accountData?.cod_receiver
      );
    },
  },
};
</script>

<style scoped>
.allegro-account-tabs-container {
  display: block !important;
  visibility: visible !important;
}

.allegro-account-tabs {
  display: block !important;
  visibility: visible !important;
  min-height: 42px;
  margin-bottom: 0;
}

.allegro-account-tabs > li {
  display: block !important;
  float: left;
}

.allegro-account-tabs > li > a {
  display: block !important;
  /* Bootstrap 5 underlines links by default - the tabs (together with the
     unread message and offer counters) should look like in the old admin
     panel on Bootstrap 3, i.e. without an underline */
  text-decoration: none;
}

.allegro-account-tabs > li > a:hover,
.allegro-account-tabs > li > a:focus {
  text-decoration: underline;
}

.tab-label {
  margin-left: 6px;
}

.tab-badge {
  margin-left: 6px;
}
</style>
